import { useMemo, useState } from "react";
import { useNotification } from "../../../../hooks/useNotification";
import { useUpdateDeliveryStatusMutation } from "../../../../store/apiSlice/shipmentApiSlice";
import { componentWithCondition } from "../../../newLayout/page/components/hoc/componentWithConditionCheck";
import { ConfirmationModal } from "../../../../components/organisms/Modal/confirmationModal";
import { useNavigate } from "react-router-dom";
import { ButtonSpinner } from "../../../../components/atoms/Button/Button.styles";
import { ListActionBurgerMenu } from "../../../newLayout/page/components/OptionsForView/ListActionBurgerMenu";

const DeliveryAction = ({ selectedOrderId, setSelectedOrder, selectedOrderFromListview }) => {
  const notify = useNotification();
  const navigate = useNavigate();
  const [updateDeliveryStatus, { isLoading: isUpdatingStatus } = {}] =
    useUpdateDeliveryStatusMutation();
  const [activeStatusTarget, setActiveStatusTarget] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);

  const handleUpdateStatus = async (statusId, successMessage) => {
    if (selectedOrderId.length !== 1) return;
    const id = selectedOrderId[0];
    setActiveStatusTarget(statusId);
    try {
      await updateDeliveryStatus({ id, status_id: statusId }).unwrap();
      notify({
        type: "success",
        message: successMessage,
      });
      setSelectedOrder([]);
    } catch (error) {
      notify({
        type: "error",
        message: error.data?.detail || `Failed to update delivery status to ${statusId}.`,
      });
    } finally {
      setActiveStatusTarget(null);
    }
  };

  const handleCancelSubmit = async () => {
    setIsCancelling(false);
    await handleUpdateStatus("cancelled", "Delivery cancelled successfully.");
  };

  const handleBook = async () => {
    await handleUpdateStatus("booked", "Delivery booked successfully.");
  };

  const handleReadyForDispatch = async () => {
    await handleUpdateStatus(
      "ready_for_dispatch",
      "Delivery marked as ready for dispatch successfully.",
    );
  };

  const hasSelection = selectedOrderId.length > 0;
  const isSingleSelection = selectedOrderId.length === 1;

  const canBook = useMemo(() => {
    if (!isSingleSelection) return false;
    const status = selectedOrderFromListview[0]?.status?.id?.toLowerCase();
    return status === "created";
  }, [isSingleSelection, selectedOrderFromListview]);

  const canCancel = useMemo(() => {
    if (!isSingleSelection) return false;
    const status = selectedOrderFromListview[0]?.status?.id?.toLowerCase();
    return ["created", "booked", "ready_for_dispatch"].includes(status);
  }, [isSingleSelection, selectedOrderFromListview]);

  const canReadyForDispatch = useMemo(() => {
    if (!isSingleSelection) return false;
    const status = selectedOrderFromListview[0]?.status?.id?.toLowerCase();
    return status === "booked";
  }, [isSingleSelection, selectedOrderFromListview]);

  const {
    isDisabledCreateShipment,
    createShipmentTitle
  } = useMemo(() => {
    if (selectedOrderFromListview.length === 0) {
      return { isDisabledCreateShipment: true, createShipmentTitle: "" };
    }

    const invalidStatus = selectedOrderFromListview.some(item => {
      const statusId = (item.status?.id || item.status || "").toLowerCase();
      return statusId !== "created" && statusId !== "booked" && statusId !== "ready_for_dispatch";
    });

    if (invalidStatus) {
      return {
        isDisabledCreateShipment: true,
        createShipmentTitle: "Only deliveries with a status of CREATED, BOOKED, or READY FOR DISPATCH can be grouped into a shipment."
      };
    }

    const first = selectedOrderFromListview[0];
    const firstCust = first.customer?.id || first.customer_id;
    const firstWh = first.from_warehouse?.id || first.from_warehouse_id;
    const firstShipTo = first.ship_to?.id || first.ship_to_id;
    const firstMove = first.movement_type?.id || first.movement_type;
    const firstDoc = first.document_type?.id || first.document_type;

    const attributesMismatch = selectedOrderFromListview.some(item => {
      const cust = item.customer?.id || item.customer_id;
      const wh = item.from_warehouse?.id || item.from_warehouse_id;
      const shipTo = item.ship_to?.id || item.ship_to_id;
      const move = item.movement_type?.id || item.movement_type;
      const doc = item.document_type?.id || item.document_type;

      return (
        cust !== firstCust ||
        wh !== firstWh ||
        shipTo !== firstShipTo ||
        move !== firstMove ||
        doc !== firstDoc
      );
    });

    if (attributesMismatch) {
      return {
        isDisabledCreateShipment: true,
        createShipmentTitle: "Selected deliveries must share the exact same Customer, Origin Facility, Destination, Movement Type, and Document Type."
      };
    }

    return { isDisabledCreateShipment: false, createShipmentTitle: "" };
  }, [selectedOrderFromListview]);

  const handleCreateShipmentFromDeliveries = () => {
    if (isDisabledCreateShipment) return;

    // Redirect to create page and pass selected delivery data
    navigate("/shipment/create", {
      state: { selectedDeliveries: selectedOrderFromListview },
    });
  };

  const isCancellingStatus = isUpdatingStatus && activeStatusTarget === "cancelled";
  const isBookingStatus = isUpdatingStatus && activeStatusTarget === "booked";
  const isRfdStatus = isUpdatingStatus && activeStatusTarget === "ready_for_dispatch";

  const burgerItems = useMemo(
    () => [
      {
        key: "cancel",
        label: "Cancel",
        onClick: () => setIsCancelling(true),
        disabled: !canCancel || isCancellingStatus,
        title:
          !canCancel && hasSelection
            ? isSingleSelection
              ? "Cannot cancel an already cancelled delivery"
              : "Only one delivery can be cancelled at a time"
            : !hasSelection
              ? "Select a delivery to cancel."
              : "",
        isLoading: isCancellingStatus,
        isDanger: true,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canCancel, isCancellingStatus, hasSelection, isSingleSelection, selectedOrderId],
  );

  return (
    <>
      <li className={`tableButton ${canBook && !isBookingStatus ? "activeState" : "disableState"}`}>
        <button
          onClick={handleBook}
          disabled={!canBook || isBookingStatus}
          title={
            !canBook && hasSelection
              ? isSingleSelection
                ? "Only allowed for deliveries in CREATED status"
                : "Only one delivery can be booked at a time"
              : !hasSelection
                ? "Select a delivery in CREATED status to book."
                : ""
          }
          style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
        >
          {isBookingStatus && <ButtonSpinner aria-hidden="true" />}
          {isBookingStatus ? "Booking…" : "Booked"}
        </button>
      </li>
      <li className={`tableButton ${canReadyForDispatch && !isRfdStatus ? "activeState" : "disableState"}`}>
        <button
          onClick={handleReadyForDispatch}
          disabled={!canReadyForDispatch || isRfdStatus}
          title={
            !canReadyForDispatch && hasSelection
              ? isSingleSelection
                ? "Only allowed for deliveries in BOOKED status"
                : "Only one delivery can be marked as ready for dispatch at a time"
              : !hasSelection
                ? "Select a delivery in BOOKED status to mark ready."
                : ""
          }
          style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
        >
          {isRfdStatus && <ButtonSpinner aria-hidden="true" />}
          {isRfdStatus ? "Updating…" : "Ready for Dispatch"}
        </button>
      </li>

      <li className={`tableButton ${isDisabledCreateShipment ? "disableState" : "activeState"}`}>
        <button
          onClick={handleCreateShipmentFromDeliveries}
          disabled={isDisabledCreateShipment}
          title={createShipmentTitle || (!hasSelection ? "Select deliveries to create a shipment." : "")}
        >
          Create Shipment
        </button>
      </li>

      <ListActionBurgerMenu items={burgerItems} />

      {isCancelling && (
        <ConfirmationModal
          titleOfModal="Cancel Delivery"
          btnName="Cancel Delivery"
          closeModal={() => {
            setIsCancelling(false);
          }}
          handleModalWork={handleCancelSubmit}
          isLoading={isCancellingStatus}
          description={<>Are you sure you want to cancel the selected delivery?</>}
        />
      )}
    </>
  );
};

export const DeliveryActionWithCheck = componentWithCondition(DeliveryAction);
